import {
  X,
  Trash,
  Plus,
  Info,
  Settings,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import React, { useState, useEffect, useRef, useCallback, memo } from 'react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription, // Add this import
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface DataField {
  value: string
  enabled: boolean
  label?: string
  description?: string
  permissions?: {
    readable: boolean
    writable: boolean
  }
  preselected?: boolean
  lastUpdated?: number // Timestamp for when this field was last updated
}

export interface NodeDataSettings {
  inputLinks: {
    nodeId: string
    nodeName: string
    enabled: boolean
    nodeType?: string
    data: Record<string, DataField>
    lastSynced?: number // Timestamp for when this link was last synced
  }[]
  outputLinks: {
    nodeId: string
    nodeName: string
    nodeType?: string
    fields: string[] // List of fields this node is using
  }[]
  // Remove availableNodes from persistent storage - compute dynamically instead
  documentSections?: {
    fullDocument: DataField
    sections: Record<string, DataField>
    enabled: boolean
  }
  exposedData?: Record<string, DataField>
  autoSync?: boolean // New setting to enable automatic synchronization
}

interface NodeDataSettingsDialogProps {
  visible: boolean
  onHide: () => void
  settings: NodeDataSettings
  nodeId: string
  onSettingsChange: (settings: NodeDataSettings) => void
  onRemoveInputLink?: (nodeId: string) => void
  onSyncInputLink?: (
    nodeId: string
  ) => Promise<{ success: boolean; newData?: Record<string, DataField> }> // New prop to handle link data sync
  forceShowAvailableNodes?: boolean
  debug?: boolean
}

const NodeDataSettingsDialog: React.FC<NodeDataSettingsDialogProps> = ({
  visible,
  onHide,
  settings,
  nodeId,
  onSettingsChange,
  onRemoveInputLink,
  onSyncInputLink,
  forceShowAvailableNodes = false,
  debug = false,
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [syncingNodeId, setSyncingNodeId] = useState<string | null>(null)
  const [syncResult, setSyncResult] = useState<{
    nodeId: string
    success: boolean
    timestamp: number
    changes?: any
    message?: string
    error?: string
  } | null>(null)
  // Add state for dynamically computed available nodes
  const [availableNodes, setAvailableNodes] = useState<
    {
      nodeId: string
      nodeName: string
      nodeType?: string
      enabled: boolean
      data: Record<string, DataField>
    }[]
  >([])
  // Add state to preserve checkbox states across regenerations
  const [availableNodesCheckboxStates, setAvailableNodesCheckboxStates] =
    useState<Record<string, Record<string, boolean>>>({})
  const changesPanelRef = useRef<OverlayPanel>(null)
  const syncButtonRefs = useRef<Record<string, React.RefObject<HTMLButtonElement>>>({})

  // Keep track of outdated nodes that need syncing
  const [outdatedNodes, setOutdatedNodes] = useState<Map<string, string[]>>(
    new Map()
  )

  // Sync local state if settings prop changes
  useEffect(() => {
    // If you have other local state that mirrors settings, update it here.
    // For example, reset syncResult and syncingNodeId when settings change.
    setSyncResult(null)
    setSyncingNodeId(null)
    // Optionally, reset outdatedNodes if needed:
    // setOutdatedNodes(new Map())
  }, [settings])

  // Initialize refs for sync buttons
  useEffect(() => {
    if (settings.inputLinks) {
      // Create refs for all input links
      const newRefs: Record<string, React.RefObject<HTMLButtonElement>> =
        {}
      settings.inputLinks.forEach((link) => {
        newRefs[link.nodeId] =
          syncButtonRefs.current[link.nodeId] || React.createRef()
      })
      syncButtonRefs.current = newRefs
    }
  }, [settings.inputLinks])

  useEffect(() => {
    if (visible) {
      computeAvailableNodes()
      checkForOutdatedContent()
    }
  }, [visible])

  // Compute available nodes dynamically when dialog opens
  const computeAvailableNodes = useCallback(() => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []
    const linkedNodeIds = settings.inputLinks.map((link) => link.nodeId)

    return allNodes
      .filter(
        (node: any) => node.id !== nodeId && !linkedNodeIds.includes(node.id)
      )
      .map((node: any) => {
        const nodeData: Record<string, DataField> = {
          nodeId: {
            value: node.id,
            enabled: availableNodesCheckboxStates[node.id]?.nodeId ?? false,
            label: 'Node ID',
          },
          nodeType: {
            value: node.type || 'unknown',
            enabled: availableNodesCheckboxStates[node.id]?.nodeType ?? false,
            label: 'Node Type',
          },
        }

        // Add full document if enabled
        if (
          node.data?.contentMarkdown &&
          node.data?.dataSettings?.documentSections?.fullDocument?.enabled
        ) {
          nodeData.fullDocument = {
            value: node.data.contentMarkdown,
            enabled:
              availableNodesCheckboxStates[node.id]?.fullDocument ?? false,
            label: 'Full Document Content',
            description: 'The complete document content',
          }
        }

        // Add enabled sections
        if (node.data?.dataSettings?.documentSections?.sections) {
          Object.entries(node.data.dataSettings.documentSections.sections)
            .filter(([_, section]: [string, any]) => section.enabled)
            .forEach(([title, section]: [string, any]) => {
              const fieldKey = `section:${title}`
              nodeData[fieldKey] = {
                value: section.value || '',
                enabled:
                  availableNodesCheckboxStates[node.id]?.[fieldKey] ?? false,
                label: `Section: ${title}`,
                description: `Document section: ${title}`,
              }
            })
        }

        // Add exposed data fields
        if (node.data?.dataSettings?.exposedData) {
          Object.entries(node.data.dataSettings.exposedData)
            .filter(
              ([_, field]: [string, any]) =>
                field.enabled && field.permissions?.readable
            )
            .forEach(([key, field]: [string, any]) => {
              const fieldKey = `exposed:${key}`
              nodeData[fieldKey] = {
                value: field.value || '',
                enabled:
                  availableNodesCheckboxStates[node.id]?.[fieldKey] ?? false,
                label: `${field.label || key} (Exposed)`,
                description: field.description || `Exposed field: ${key}`,
                permissions: field.permissions || {
                  readable: true,
                  writable: false,
                },
              }
            })
        }

        return {
          nodeId: node.id,
          nodeName: node.data?.label || node.id,
          enabled: true,
          nodeType: node.type || 'Unknown',
          data: nodeData,
        }
      })
  }, [nodeId, settings.inputLinks, availableNodesCheckboxStates])

  // Update available nodes when dialog opens or input links change
  useEffect(() => {
    if (visible) {
      setAvailableNodes(computeAvailableNodes())
      checkForOutdatedContent()
    }
  }, [visible, computeAvailableNodes])

  // Handle toggling auto-sync feature
  const handleAutoSyncToggle = (checked: boolean) => {
    const updatedSettings = {
      ...settings,
      autoSync: checked,
    }
    onSettingsChange(updatedSettings)

    if (debug) {
      console.log(`Auto-sync ${checked ? 'enabled' : 'disabled'}`)
    }
  }

  // Format timestamp with date and time
  const formatTimestamp = (timestamp: number): string => {
    if (!timestamp) return 'Never'

    const date = new Date(timestamp)
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Compare old and new data to find differences
  const findDataChanges = (
    oldData: Record<string, DataField>,
    newData: Record<string, DataField>
  ) => {
    const changes = {
      added: [] as string[],
      removed: [] as string[],
      modified: [] as { key: string; oldValue: string; newValue: string }[],
    }

    // Check for added or modified fields
    Object.entries(newData).forEach(([key, field]) => {
      // Don't report exposed fields as changes if only timestamps are different
      const isExposedField = key.startsWith('exposed:')

      if (!oldData[key]) {
        changes.added.push(key)
      } else if (oldData[key].value !== field.value) {
        // For exposed fields, verify they actually changed content
        if (!isExposedField || oldData[key].value !== field.value) {
          changes.modified.push({
            key,
            oldValue: oldData[key].value,
            newValue: field.value,
          })
        }
      }
    })

    // Check for removed fields
    Object.keys(oldData).forEach((key) => {
      if (!newData[key]) {
        changes.removed.push(key)
      }
    })

    return changes
  }

  // Check all input links for outdated content with more detailed change information
  const checkForOutdatedContent = async () => {
    if (!settings.inputLinks?.length) {
      return
    }

    try {
      const allNodes = (window as any).__flowGetAllNodes?.() || []

      const outdatedNodesMap = new Map<string, string[]>()

      for (const link of settings.inputLinks) {
        const sourceNode = allNodes.find((node: any) => node.id === link.nodeId)
        if (!sourceNode) {
          continue
        }

        const changedFields = await checkLinkChanges(link, sourceNode)
        if (changedFields.length > 0) {
          outdatedNodesMap.set(link.nodeId, changedFields)
        }
      }

      setOutdatedNodes(new Map(outdatedNodesMap)) // Ensure new Map instance
    } catch (error) {
      console.error('Error checking for outdated content:', error)
    }
  }

  useEffect(() => {
    // Remove: if (debug) { console.log('Current outdatedNodes:', outdatedNodes, 'Size:', outdatedNodes.size) }
  }, [outdatedNodes, debug])

  // Get specific changes between source node and current link
  const checkLinkChanges = async (
    link: any,
    sourceNode: any
  ): Promise<string[]> => {
    const changedFields: string[] = []

    try {
      const oldLinkData = link.data || {}
      const sourceData = {
        ...(sourceNode.data?.dataSettings?.exposedData || {}),
        ...(sourceNode.data?.dataSettings?.documentSections?.sections
          ? Object.fromEntries(
              Object.entries(
                sourceNode.data.dataSettings.documentSections.sections
              ).map(([title, section]: [string, any]) => [
                `section:${title}`,
                section,
              ])
            )
          : {}),
        fullDocument: sourceNode.data?.contentMarkdown
          ? { value: sourceNode.data.contentMarkdown, enabled: true }
          : oldLinkData.fullDocument || { value: '', enabled: false },
      }

      const sourceLastModified = sourceNode.data?.lastModified || 0
      const linkLastSynced = link.lastSynced || 0

      if (sourceLastModified > linkLastSynced) {
        Object.keys(sourceData).forEach((key) => {
          const exposedKey =
            key.startsWith('section:') || key === 'fullDocument'
              ? key
              : `exposed:${key}`
          const oldField = oldLinkData[exposedKey]
          const newField = sourceData[key]

          if (!oldField) {
            changedFields.push(key)
          } else if (oldField.value !== newField.value) {
            changedFields.push(key)
          }
        })
      } else {
        // Detailed comparison if timestamps are equal or older
        if (sourceNode.data?.dataSettings?.documentSections?.sections) {
          const sections =
            sourceNode.data.dataSettings.documentSections.sections
          Object.entries(sections).forEach(
            ([title, section]: [string, any]) => {
              const sectionKey = `section:${title}`
              const linkSection = oldLinkData[sectionKey]
              if (!linkSection || linkSection.value !== section.value) {
                changedFields.push(sectionKey)
              }
            }
          )
        }

        if (
          sourceNode.data?.contentMarkdown &&
          oldLinkData.fullDocument?.value !== sourceNode.data.contentMarkdown
        ) {
          changedFields.push('fullDocument')
        }

        if (sourceNode.data?.dataSettings?.exposedData) {
          const exposedData = sourceNode.data.dataSettings.exposedData
          Object.entries(exposedData).forEach(([key, field]: [string, any]) => {
            const exposedKey = `exposed:${key}`
            const linkField = oldLinkData[exposedKey]
            if (!linkField || linkField.value !== field.value) {
              changedFields.push(key)
            }
          })
        }
      }

      return changedFields
    } catch (error) {
      console.error(`Error checking changes for node ${link.nodeId}:`, error)
      return changedFields
    }
  }

  // Add a helper function to create a simple hash of content for comparison
  const hashString = (str: string): string => {
    let hash = 0
    if (str.length === 0) return hash.toString()
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32bit integer
    }
    return hash.toString()
  }

  // Handle manual sync for a specific input link with better error handling
  const handleSyncInputLink = async (linkNodeId: string) => {
    if (!onSyncInputLink) {
      console.error('No sync handler provided')
      return
    }

    try {
      setSyncingNodeId(linkNodeId)

      const linkIndex = settings.inputLinks.findIndex(
        (link) => link.nodeId === linkNodeId
      )
      if (linkIndex === -1) {
        throw new Error(`Node ${linkNodeId} not found in input links`)
      }

      const oldLinkData = settings.inputLinks[linkIndex].data || {}
      const result = await onSyncInputLink(linkNodeId)
      const success = result.success !== false
      const newData = result.newData || {}

      if (success) {
        // Fully merge all fields from newData into oldLinkData
        const mergedData = { ...oldLinkData }
        Object.entries(newData).forEach(([key, field]) => {
          mergedData[key] = { ...field } // Copy all fields, preserving structure
        })

        const updatedInputLinks = [...settings.inputLinks]
        updatedInputLinks[linkIndex] = {
          ...updatedInputLinks[linkIndex],
          data: mergedData,
          lastSynced: Date.now(),
        }

        const updatedSettings = { ...settings, inputLinks: updatedInputLinks }
        onSettingsChange(updatedSettings)

        const changes = findDataChanges(oldLinkData, newData)
        const hasChanges =
          changes.added.length > 0 ||
          changes.removed.length > 0 ||
          changes.modified.length > 0

        setOutdatedNodes((prev) => {
          const updated = new Map(prev)
          updated.delete(linkNodeId)
          return updated
        })

        const syncResultObj = {
          nodeId: linkNodeId,
          success: true,
          timestamp: Date.now(),
          changes: hasChanges ? changes : null,
        }

        setSyncResult(syncResultObj)

        if (
          hasChanges &&
          changesPanelRef.current &&
          syncButtonRefs.current[linkNodeId]?.current
        ) {
          setTimeout(() => {
            try {
              changesPanelRef.current?.show({
                target: syncButtonRefs.current[linkNodeId].current,
                event: {} as React.SyntheticEvent,
              })
            } catch (panelError) {
              console.error('Error showing changes panel:', panelError)
            }
          }, 100)
        }
      } else {
        const syncResultObj = {
          nodeId: linkNodeId,
          success: false,
          timestamp: Date.now(),
          message: result.error || 'Sync operation returned failure',
        }
        setSyncResult(syncResultObj)
      }

      setTimeout(() => {
        setSyncResult(null)
      }, 5000)
    } catch (error) {
      console.error(`Error syncing data from node ${linkNodeId}:`, error)
      const syncResultObj = {
        nodeId: linkNodeId,
        success: false,
        timestamp: Date.now(),
        error: error.message || 'Unknown error during sync',
      }
      setSyncResult(syncResultObj)
      setTimeout(() => {
        setSyncResult(null)
      }, 5000)
    } finally {
      setSyncingNodeId(null)
    }
  }

  // Check if a link's data might be out of date (more than 5 minutes old)
  const isLinkDataStale = (link: any): boolean => {
    if (!link.lastSynced) return true

    const now = Date.now()
    const fiveMinutes = 5 * 60 * 1000
    return now - link.lastSynced > fiveMinutes
  }

  // Handler for adding node to input links
  const handleAddNode = (node: any) => {
    const updatedSettings = { ...settings }

    // Add to input links with a deep copy to avoid reference issues
    updatedSettings.inputLinks = [
      ...settings.inputLinks,
      {
        ...node,
        data: { ...node.data },
      },
    ]

    // DON'T clear checkbox states - keep them for when the node becomes available again
    // DON'T remove the node from available nodes - let the computeAvailableNodes filter handle it
    // The node will automatically be filtered out by computeAvailableNodes due to being in inputLinks

    onSettingsChange(updatedSettings)

    // Force recompute available nodes to reflect the change without losing checkbox states
    setTimeout(() => {
      setAvailableNodes(computeAvailableNodes())
    }, 0)
  }

  // Handler for input link field toggle
  const handleInputFieldToggle = (
    nodeId: string,
    fieldKey: string,
    checked: boolean
  ) => {
    const updatedSettings = { ...settings }
    const nodeIndex = updatedSettings.inputLinks.findIndex(
      (link) => link.nodeId === nodeId
    )

    if (
      nodeIndex !== -1 &&
      updatedSettings.inputLinks[nodeIndex].data[fieldKey]
    ) {
      updatedSettings.inputLinks[nodeIndex].data[fieldKey].enabled = checked
      onSettingsChange(updatedSettings)
    }
  }

  // Handler for input link enable/disable toggle
  const handleInputLinkToggle = (nodeId: string, checked: boolean) => {
    const updatedSettings = { ...settings }
    const nodeIndex = updatedSettings.inputLinks.findIndex(
      (link) => link.nodeId === nodeId
    )

    if (nodeIndex !== -1) {
      updatedSettings.inputLinks[nodeIndex].enabled = checked
      onSettingsChange(updatedSettings)
    }
  }

  // Toggle available field in settings - now updates persistent checkbox state
  const handleAvailableFieldToggle = (
    nodeId: string,
    fieldKey: string,
    checked: boolean
  ) => {
    // Update persistent checkbox states
    setAvailableNodesCheckboxStates((prev) => ({
      ...prev,
      [nodeId]: {
        ...prev[nodeId],
        [fieldKey]: checked,
      },
    }))

    // Update current available nodes state
    setAvailableNodes((prev) =>
      prev.map((node) =>
        node.nodeId === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                [fieldKey]: {
                  ...node.data[fieldKey],
                  enabled: checked,
                },
              },
            }
          : node
      )
    )
  }

  // Handle toggle for exposed data fields
  const handleExposedFieldToggle = (fieldKey: string, checked: boolean) => {
    if (!settings.exposedData) return

    const updatedSettings = { ...settings }
    if (updatedSettings.exposedData?.[fieldKey]) {
      updatedSettings.exposedData[fieldKey].enabled = checked
      onSettingsChange(updatedSettings)
    }
  }

  // Handle toggle for exposed data field permission
  const handleExposedFieldPermissionToggle = (
    fieldKey: string,
    permission: 'readable' | 'writable',
    checked: boolean
  ) => {
    if (!settings.exposedData) return

    const updatedSettings = { ...settings }
    if (
      updatedSettings.exposedData?.[fieldKey]?.permissions
    ) {
      updatedSettings.exposedData[fieldKey].permissions[permission] = checked
      onSettingsChange(updatedSettings)
    }
  }

  // Handle toggle for section in document sections
  const handleSectionToggle = (sectionTitle: string, checked: boolean) => {
    if (!settings.documentSections) return

    const updatedSettings = { ...settings }
    if (updatedSettings.documentSections?.sections[sectionTitle]) {
      updatedSettings.documentSections.sections[sectionTitle].enabled = checked
      onSettingsChange(updatedSettings)
    }
  }

  // Handle toggle for full document in document sections
  const handleFullDocumentToggle = (checked: boolean) => {
    if (!settings.documentSections) return

    const updatedSettings = { ...settings }
    if (updatedSettings.documentSections) {
      updatedSettings.documentSections.fullDocument.enabled = checked
      onSettingsChange(updatedSettings)
    }
  }

  // Render changes overlay panel with more detailed information
  const renderChangesPanel = () => {
    if (!syncResult?.changes) return null

    const { changes } = syncResult
    const hasChanges =
      changes.added.length > 0 ||
      changes.removed.length > 0 ||
      changes.modified.length > 0

    if (!hasChanges) return null

    return (
      <div className="p-2">
        <h4 className="text-md font-semibold mb-2">Changes Detected</h4>

        {changes.modified.length > 0 && (
          <div className="mb-3">
            <div className="text-sm font-medium text-blue-600 mb-1">
              Modified Fields:
            </div>
            <div className="bg-gray-50 p-2 rounded">
              {changes.modified.map((item) => (
                <Card key={`modified-${item.key}`} className="mb-2">
                  <div className="text-sm font-medium mb-1">
                    {getFieldDisplayName(item.key)}
                  </div>
                  <div className="text-xs bg-red-50 p-1 mb-1 rounded line-through">
                    Old: {item.oldValue.substring(0, 50)}
                    {item.oldValue.length > 50 ? '...' : ''}
                  </div>
                  <div className="text-xs bg-green-50 p-1 rounded">
                    New: {item.newValue.substring(0, 50)}
                    {item.newValue.length > 50 ? '...' : ''}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {changes.added.length > 0 && (
          <div className="mb-2">
            <div className="text-sm font-medium text-green-600 mb-1">
              Added Fields:
            </div>
            <ul className="list-disc pl-4">
              {changes.added.map((key) => (
                <li key={`added-${key}`} className="text-sm">
                  {getFieldDisplayName(key)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {changes.removed.length > 0 && (
          <div className="mb-2">
            <div className="text-sm font-medium text-red-600 mb-1">
              Removed Fields:
            </div>
            <ul className="list-disc pl-4">
              {changes.removed.map((key) => (
                <li key={`removed-${key}`} className="text-sm">
                  {getFieldDisplayName(key)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  // Helper to get user-friendly field names
  const getFieldDisplayName = (key: string): string => {
    if (key === 'fullDocument') return 'Full Document'
    if (key.startsWith('section:'))
      return `Section: ${key.replace('section:', '')}`
    if (key.startsWith('exposed:'))
      return `${key.replace('exposed:', '')} (Exposed)`
    if (key === 'general_update') return 'General Content Update'
    return key
  }

  // Render input links with sync button, change detection and specific outdated field info
  const renderInputLinks = () => {
    if (!settings.inputLinks || settings.inputLinks.length === 0) {
      return (
        <p className="text-gray-500 p-3">
          No nodes connected. Add some from the available nodes tab.
        </p>
      )
    }

    return (
      <div className="space-y-4">
        {settings.inputLinks.map((link) => {
          // Separate section fields from regular fields
          const sectionFields = {}
          const regularFields = {}

          // Group fields by type
          Object.entries(link.data || {}).forEach(([key, field]) => {
            if (!field) return // <-- Add this guard
            if (key.startsWith('section:')) {
              sectionFields[key] = field
            } else {
              regularFields[key] = field
            }
          })

          // Check if this link's data is stale or has specific outdated fields
          const outdatedFields = outdatedNodes.get(link.nodeId) || []
          const needsSync = outdatedFields.length > 0
          const isSyncing = syncingNodeId === link.nodeId
          const syncStatus =
            syncResult && syncResult.nodeId === link.nodeId ? syncResult : null
          //const syncStatus = syncResult && syncResult.nodeId === link.nodeId

          return (
            <div
              key={link.nodeId}
              className={`border rounded p-3 ${
                needsSync ? 'bg-yellow-50' : 'bg-gray-50'
              } mb-4`}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center">
                  <Checkbox
                    id={`link-enabled-${link.nodeId}`}
                    checked={link.enabled}
                    onCheckedChange={(e) =>
                      handleInputLinkToggle(link.nodeId, e.checked)
                    }
                    className="mr-2"
                  />
                  <label
                    htmlFor={`link-enabled-${link.nodeId}`}
                    className="font-bold"
                  >
                    {link.nodeName || link.nodeId}{' '}
                    {debug && `(${link.nodeType || 'unknown type'})`}
                  </label>

                  {/* Show outdated data indicator */}
                  {needsSync && (
                    <Badge
                      value={`${outdatedFields.length} update${
                        outdatedFields.length > 1 ? 's' : ''
                      }`}
                      severity="warning"
                      className="ml-2"
                      // Remove tooltipOptions, keep only tooltip if you want a title
                      tooltip={`Fields with updates: ${outdatedFields
                        .map(getFieldDisplayName)
                        .join(', ')}`}
                    />
                  )}
                </div>
                <div className="flex items-center">
                  {/* Add sync button with ref */}
                  {onSyncInputLink && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            ref={syncButtonRefs.current[link.nodeId]}
                            variant={needsSync ? 'warning' : 'ghost'}
                            size="sm"
                            className="mr-2"
                            disabled={isSyncing}
                            onClick={() => handleSyncInputLink(link.nodeId)}
                          >
                            {isSyncing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          <p>
                            {needsSync
                              ? `${outdatedFields.length} field${
                                  outdatedFields.length > 1 ? 's' : ''
                                } need${
                                  outdatedFields.length > 1 ? '' : 's'
                                } updating: ${outdatedFields
                                  .map(getFieldDisplayName)
                                  .join(', ')}`
                              : 'Sync latest data from this node'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {/* Show sync result notification */}
                  {renderSyncStatus(link, syncStatus, isSyncing)}

                  {onRemoveInputLink && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemoveInputLink(link.nodeId)}
                      title="Remove this link"
                    >
                      <Trash className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
              {/* Last synced timestamp with date */}
              {link.lastSynced && (
                <div className="text-xs text-gray-500 mb-2">
                  Last updated: {formatTimestamp(link.lastSynced)}
                </div>
              )}
              {/* Display specific outdated fields if any */}
              {needsSync && (
                <div className="mb-3 text-xs bg-yellow-50 p-2 border border-yellow-200 rounded">
                  <div className="font-medium">Updated content available:</div>
                  <ul className="list-disc pl-4 mt-1">
                    {outdatedFields.map((field) => (
                      <li key={field}>{getFieldDisplayName(field)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Show regular fields */}
              {Object.keys(regularFields).length > 0 && (
                <div className="mt-2">
                  <div className="text-sm font-medium mb-2">
                    Standard Fields:
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(regularFields).map(([key, field]) => {
                      if (!field) return null // <-- Add this guard
                      return (
                        <div key={key} className="flex items-center py-1">
                          <Checkbox
                            id={`link-${link.nodeId}-${key}`}
                            checked={field.enabled}
                            onCheckedChange={(checked) =>
                              handleInputFieldToggle(link.nodeId, key, checked)
                            }
                            className="mr-2"
                          />
                          <label
                            htmlFor={`link-${link.nodeId}-${key}`}
                            className="text-sm cursor-pointer"
                            title={field.description || key}
                          >
                            {field.label || key}
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Show document sections separately with a clear header */}
              {Object.keys(sectionFields).length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-300">
                  <div className="text-sm font-medium mb-2 text-blue-600">
                    Node Sections:
                  </div>
                  <Accordion type="single" collapsible>
                    <AccordionItem value="available-sections">
                      <AccordionTrigger>
                        View Available Sections
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(sectionFields).map(([key, field]) => {
                            if (!field) return null // <-- Add this guard
                            return (
                              <div
                                key={key}
                                className="flex items-center py-1 bg-gray-100 px-2 rounded"
                              >
                                <Checkbox
                                  id={`link-section-${link.nodeId}-${key}`}
                                  checked={field.enabled}
                                  onCheckedChange={(e) =>
                                    handleInputFieldToggle(
                                      link.nodeId,
                                      key,
                                      e.checked
                                    )
                                  }
                                  className="mr-2"
                                />
                                <label
                                  htmlFor={`link-section-${link.nodeId}-${key}`}
                                  className="text-sm cursor-pointer font-medium"
                                  title={field.description || ''}
                                >
                                  {field.label || key.replace('section:', '')}
                                </label>
                              </div>
                            )
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  // Render available nodes that can be linked
  const renderAvailableNodes = () => {
    if (debug) {
      console.log('Available nodes:', availableNodes?.length || 0)
    }

    if (!availableNodes || availableNodes.length === 0) {
      return (
        <p className="text-gray-500 p-3">
          No additional nodes available to link.
        </p>
      )
    }

    return (
      <div className="space-y-4">
        {availableNodes.map((node) => (
          <div key={node.nodeId} className="border rounded p-3 bg-gray-50 mb-4">
            <div className="flex justify-between items-center mb-2">
              <div className="font-bold">
                {node.nodeName || node.nodeId}{' '}
                {debug && `(${node.nodeType || 'unknown'})`}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddNode(node)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Link
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-2">
              {Object.entries(node.data || {}).map(
                ([key, field]: [string, any]) => (
                  <div key={key} className="flex items-center">
                    <Checkbox
                      id={`avail-${node.nodeId}-${key}`}
                      checked={field.enabled}
                      onCheckedChange={(checked) =>
                        handleAvailableFieldToggle(node.nodeId, key, checked)
                      }
                      className="mr-2"
                    />
                    <label
                      htmlFor={`avail-${node.nodeId}-${key}`}
                      className="text-sm"
                      title={field.description || key}
                    >
                      {field.label || key}
                    </label>
                  </div>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Render exposed data fields that can be accessed by other nodes
  const renderExposedData = () => {
    if (
      !settings.exposedData ||
      Object.keys(settings.exposedData).length === 0
    ) {
      return (
        <p className="text-gray-500 p-3">
          No information available for exposure.
        </p>
      )
    }

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-bold mb-4">Exposable information</h3>
        <p className="text-sm text-gray-600 mb-4">
          These fields can be accessed by other nodes when they connect to this
          node.
        </p>

        {Object.entries(settings.exposedData).map(([key, field]) => (
          <div key={key} className="border rounded p-3 bg-gray-50 mb-4">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center">
                <Checkbox
                  id={`exposed-${key}`} // Changed from inputId to id
                  checked={field.enabled}
                  onCheckedChange={(checked) =>
                    handleExposedFieldToggle(key, checked)
                  } // Use onCheckedChange
                  className="mr-2"
                />
                <label htmlFor={`exposed-${key}`} className="font-bold">
                  {field.label || key}
                </label>
              </div>
            </div>

            {field.description && (
              <p className="text-sm text-gray-600 mb-2">{field.description}</p>
            )}

            <div className="bg-gray-100 p-2 rounded mb-2 text-sm">
              <div className="font-mono whitespace-pre-wrap break-words">
                {typeof field.value === 'string' && field.value.length > 100
                  ? `${field.value.substring(0, 100)}...`
                  : field.value}
              </div>
            </div>

            <div className="flex gap-4 mt-2">
              <div className="flex items-center">
                <Checkbox
                  id={`exposed-${key}-readable`}
                  checked={field.permissions?.readable ?? true}
                  onCheckedChange={(checked) =>
                    handleExposedFieldPermissionToggle(key, 'readable', checked)
                  }
                  className="mr-2"
                />
                <label htmlFor={`exposed-${key}-readable`} className="text-sm">
                  Readable
                </label>
              </div>

              <div className="flex items-center">
                <Checkbox
                  id={`exposed-${key}-writable`}
                  checked={field.permissions?.writable ?? false}
                  onCheckedChange={(checked) =>
                    handleExposedFieldPermissionToggle(key, 'writable', checked)
                  }
                  className="mr-2"
                />
                <label htmlFor={`exposed-${key}-writable`} className="text-sm">
                  Writable
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Render document sections that can be shared
  const renderDocumentSections = () => {
    if (!settings.documentSections) {
      return <p className="text-gray-500 p-3">No node sections available.</p>
    }

    return (
      <div>
        <h3 className="text-lg font-bold mb-4">Node Sections</h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure which sections of the node are available to other nodes.
        </p>

        <div className="border rounded p-3 bg-gray-50 mb-4">
          <div className="flex items-center mb-2">
            <Checkbox
              id="fullDocument" // Changed from inputId to id
              checked={
                settings.documentSections?.fullDocument?.enabled ?? false
              }
              onCheckedChange={(checked) => handleFullDocumentToggle(checked)} // Use onCheckedChange
              className="mr-2"
            />
            <label htmlFor="fullDocument" className="font-bold">
              Full Document Content
            </label>
          </div>
        </div>

        <h4 className="font-bold mt-4 mb-2">Individual Sections</h4>
        {Object.entries(settings.documentSections.sections).map(
          ([title, field]) => (
            <div key={title} className="border rounded p-3 bg-gray-50 mb-2">
              <div className="flex items-center mb-2">
                <Checkbox
                  id={`section-${title}`}
                  checked={field.enabled}
                  onCheckedChange={(checked) =>
                    handleSectionToggle(title, checked)
                  } // Use onCheckedChange
                  className="mr-2"
                />
                <label htmlFor={`section-${title}`} className="font-bold">
                  {title}
                </label>
              </div>

              <div className="bg-gray-100 p-2 rounded text-sm">
                <div className="font-mono whitespace-pre-wrap break-words max-h-20 overflow-y-auto">
                  {typeof field.value === 'string' && field.value.length > 100
                    ? `${field.value.substring(0, 100)}...`
                    : field.value}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    )
  }

  // Render output links - this is READ ONLY information about which nodes use this node's data
  const renderOutputLinks = () => {
    // Check if we have any output links
    if (!settings.outputLinks || settings.outputLinks.length === 0) {
      return (
        <div>
          <h3 className="text-lg font-bold mb-4">Nodes Using This Data</h3>
          <p className="text-gray-500 p-3">
            No other nodes are currently using information from this node.
          </p>
          <div className="text-sm text-gray-600 mt-2 p-3">
            <p>
              This section shows when other nodes in the flow are accessing
              information from this node. It is read-only information to help
              you understand the information dependencies in your flow.
            </p>
          </div>
        </div>
      )
    }

    // Show the list of nodes that use this node's data
    return (
      <div>
        <h3 className="text-lg font-bold mb-4">Other nodes Using This node</h3>
        <p className="text-sm text-gray-600 mb-4">
          These nodes are currently using information exposed by this node:
        </p>

        {settings.outputLinks.map((link, index) => (
          <div key={index} className="border rounded p-3 bg-gray-50 mb-4">
            <div className="font-bold mb-2">{link.nodeName || link.nodeId}</div>
            {link.nodeType && (
              <div className="text-sm text-gray-500 mb-2">
                Type: {link.nodeType}
              </div>
            )}

            {link.fields && link.fields.length > 0 ? (
              <div className="mt-2">
                <div className="text-sm font-medium mb-1">
                  Using these fields:
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {link.fields.map((field, i) => (
                    <div
                      key={i}
                      className="text-sm py-1 px-2 bg-gray-100 rounded"
                    >
                      {field}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                No specific fields used
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  // Add auto-sync settings section
  const renderGeneralSettings = () => {
    return (
      <div className="p-4">
        <h3 className="text-lg font-bold mb-4">General Data Settings</h3>

        <div className="border rounded p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-md font-semibold">
                Automatic Information Synchronization
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                When enabled, information from linked nodes will be
                automatically synchronized when changes are detected.
              </p>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-200">
            <Switch
              id="auto-update"
              checked={settings.autoSync}
              onCheckedChange={handleAutoSyncToggle}
            />
            <div className="text-sm text-gray-700 mt-3">
              <i className="pi pi-info-circle mr-2 text-blue-500"></i>
              Checking for updates compares your linked information with the
              source Smart-Papers to detect any changes that need to be
              synchronized.
            </div>
          </div>
        </div>

        <div className="mt-4 border rounded p-4 bg-blue-50">
          <h4 className="text-md font-semibold flex items-center">
            <i className="pi pi-sync"></i>
            Manual Synchronization
          </h4>
          <p className="text-sm text-gray-700 mt-1">
            You can manually sync information from any linked node by clicking
            the sync button next to that node in the Incoming-tab.
          </p>
        </div>
      </div>
    )
  }

  // Add a "Sync All" button to update all outdated nodes at once
  const renderSyncAllButton = () => {
    if (
      outdatedNodes.size === 0 ||
      !settings.inputLinks ||
      settings.inputLinks.length === 0
    ) {
      return null
    }

    const totalUpdates = Array.from(outdatedNodes.values()).reduce(
      (sum, fields) => sum + fields.length,
      0
    )

    return (
      <div className="mb-4 flex items-center justify-between bg-yellow-50 p-3 rounded border border-yellow-200">
        <div>
          <span className="font-medium">Updates available</span>
          <p className="text-sm text-gray-600 mt-1">
            {outdatedNodes.size}{' '}
            {outdatedNodes.size === 1 ? 'node has' : 'nodes have'}{' '}
            {totalUpdates} updated field
            {totalUpdates !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          label="Sync All"
          icon="pi pi-sync"
          className="p-button-warning p-button-sm"
          onClick={async () => {
            if (!onSyncInputLink) return

            // Sync all outdated nodes one by one
            for (const nodeId of outdatedNodes.keys()) {
              await handleSyncInputLink(nodeId)
            }
          }}
        />
      </div>
    )
  }

  // Render sync result notification in input links with explicit success check
  const renderSyncStatus = (link, syncStatus, isSyncing) => {
    if (!isSyncing && !syncStatus) return null

    if (isSyncing) {
      return <span className="text-xs text-blue-600 mr-2">Syncing...</span>
    }

    // Ensure syncStatus is an object with a success property
    const isSuccessful =
      syncStatus &&
      typeof syncStatus === 'object' &&
      syncStatus.success === true

    return (
      <span
        className={`text-xs ${isSuccessful ? 'text-green-600' : 'text-red-600'} mr-2`}
        style={{
          cursor: syncStatus?.changes ? 'pointer' : 'default',
        }}
        onClick={
          syncStatus?.changes
            ? () => {
                changesPanelRef.current?.show({
                  target: syncButtonRefs.current[link.nodeId].current,
                  event: {} as React.SyntheticEvent,
                })
              }
            : undefined
        }
      >
        {isSuccessful
          ? syncStatus.changes
            ? 'Synced with changes!'
            : 'Synced!'
          : 'Sync failed'}
      </span>
    )
  }
  if (debug) {
    // Remove: console.log('Rendering TabView with outdatedNodes:', outdatedNodes, 'Size:', outdatedNodes.size)
  }

  // Return the complete dialog with standard div scrolling
  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Information Settings</DialogTitle>
          <DialogDescription>
            Configure information flows, from this node to other nodes and vice
            versa, and exposed fields for this node. Automation must be turned
            on manually.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto flex flex-col">
          <Tabs defaultValue="input" className="w-full h-full flex flex-col">
            <div className="sticky top-0 z-10 bg-white border-b">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="input">
                  {'>'} Incoming
                  {outdatedNodes.size > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      {outdatedNodes.size}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="available">Available Nodes</TabsTrigger>
                <TabsTrigger value="exposed">Exposed Information</TabsTrigger>
                {settings.documentSections && (
                  <TabsTrigger value="sections">
                    Information access rights
                  </TabsTrigger>
                )}
                <TabsTrigger value="output">{'>'} Outgoing</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-auto">
              <TabsContent value="input" className="p-4 mt-0">
                {renderSyncAllButton()}
                {renderInputLinks()}
              </TabsContent>

              <TabsContent value="available" className="p-4 mt-0">
                {renderAvailableNodes()}
              </TabsContent>

              <TabsContent value="exposed" className="p-4 mt-0">
                {renderExposedData()}
              </TabsContent>

              {settings.documentSections && (
                <TabsContent value="sections" className="p-4 mt-0">
                  {renderDocumentSections()}
                </TabsContent>
              )}

              <TabsContent value="output" className="p-4 mt-0">
                {renderOutputLinks()}
              </TabsContent>

              <TabsContent value="settings" className="p-4 mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Advanced Settings</CardTitle>
                  </CardHeader>
                  <CardContent>{renderGeneralSettings()}</CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>
        {renderChangesPanel()}

        <DialogFooter>
          {debug && (
            <div className="text-xs text-gray-500">
              Debug: {availableNodes?.length || 0} available nodes,
              {settings.inputLinks?.length || 0} input links,
              {settings.outputLinks?.length || 0} output links, Auto-sync:{' '}
              {settings.autoSync ? 'enabled' : 'disabled'}
            </div>
          )}
          <Button variant="outline" onClick={onHide}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default memo(NodeDataSettingsDialog)
